import { Chart, useChart } from '@chakra-ui/charts';
import { Box, Flex, SegmentGroup, SegmentGroupValueChangeDetails, Text } from '@chakra-ui/react';
import { formatVolume, getProbabilityHistory } from '@features/prediction-markets/fakeMarketData';
import { PredictionMarket } from '@features/prediction-markets/types';
import { memo, useCallback, useMemo, useState } from 'react';
import { Line, LineChart, Tooltip, XAxis, YAxis } from 'recharts';

interface Props {
  market: PredictionMarket;
}

const CHART_COLORS = [
  'selection.500',
  'orange.500',
  'green.500',
  'purple.500',
  'red.500',
  'teal.500',
  'yellow.500',
  'pink.500',
];

const INTERVALS = ['1H', '6H', '1D', '1W', '1M', 'ALL'];

const PriceRangeProbabilityChart = ({ market }: Props) => {
  const [interval, setInterval] = useState('ALL');
  const history = useMemo(() => getProbabilityHistory(market), [market]);

  const series = useMemo(
    () =>
      market.outcomes.map((outcome, idx) => ({
        name: outcome.label,
        color: CHART_COLORS[idx % CHART_COLORS.length],
      })),
    [market.outcomes],
  );

  const chartData = useMemo(
    () =>
      history.map((point) => {
        const entry: Record<string, string | number> = { date: point.label };
        market.outcomes.forEach((o) => {
          entry[o.label] = point.values[o.id] ?? o.probability;
        });
        return entry;
      }),
    [history, market.outcomes],
  );

  const chart = useChart({ data: chartData, series });

  const handleIntervalChange = useCallback((e: SegmentGroupValueChangeDetails) => {
    if (e.value != null) setInterval(e.value);
  }, []);

  const formattedDate = new Date(market.endDate).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

  return (
    <Flex direction="column" gap={3} bg="surface.muted" borderRadius="xl" p={5}>
      <Flex gap={3} flexWrap="wrap" alignItems="center">
        {market.outcomes.map((outcome, idx) => (
          <Flex key={outcome.id} alignItems="center" gap={1.5}>
            <Box
              w="10px"
              h="10px"
              borderRadius="full"
              bg={CHART_COLORS[idx % CHART_COLORS.length]}
              flexShrink={0}
            />
            <Text fontSize="xs" color="text.primary" fontWeight="medium">
              {outcome.label}
            </Text>
            <Text fontSize="xs" color="text.secondary">
              {outcome.probability}%
            </Text>
          </Flex>
        ))}
      </Flex>

      <Chart.Root maxH="280px" chart={chart}>
        <LineChart data={chart.data}>
          <XAxis
            dataKey={chart.key('date')}
            tickMargin={8}
            stroke={chart.color('border.light')}
            fontSize={11}
          />
          <YAxis
            stroke={chart.color('border.light')}
            domain={[0, 100]}
            tickFormatter={(v: number) => `${v}%`}
            fontSize={11}
          />
          <Tooltip animationDuration={100} cursor={false} content={<Chart.Tooltip />} />
          {chart.series.map((item) => (
            <Line
              key={item.name}
              type="monotone"
              isAnimationActive={false}
              dataKey={chart.key(item.name)}
              stroke={chart.color(item.color)}
              strokeWidth={2}
              dot={false}
            />
          ))}
        </LineChart>
      </Chart.Root>

      <Flex
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
      >
        <Flex gap={3} alignItems="center">
          <Text fontSize="xs" color="text.secondary">
            {formatVolume(market.totalVolume)} Vol.
          </Text>
          <Text fontSize="xs" color="text.secondary">
            ⏱ {formattedDate}
          </Text>
        </Flex>

        <SegmentGroup.Root value={interval} onValueChange={handleIntervalChange} size="xs">
          <SegmentGroup.Indicator bg="surface.accent" />
          {INTERVALS.map((intv) => (
            <SegmentGroup.Item key={intv} value={intv} cursor="pointer">
              <SegmentGroup.ItemText fontSize="2xs">{intv}</SegmentGroup.ItemText>
              <SegmentGroup.ItemHiddenInput />
            </SegmentGroup.Item>
          ))}
        </SegmentGroup.Root>
      </Flex>
    </Flex>
  );
};

export default memo(PriceRangeProbabilityChart);
